import type { ReactNode } from "react";

import type {
  MultiStrategyBestIdea,
  MultiStrategyScenarioCell,
  MultiStrategyScreenEntry,
  RejectedStrategyCandidate,
} from "@/domains/trade-ideas/lib/contracts";
import { BE_ADR_TONE_CLASS } from "@/domains/trade-ideas/lib/deskThresholdPresentation";
import {
  beAdrRowView,
  axisReasonCodeLabel,
  carryBreakevenCopy,
  earningsExitClockCopy,
  EVENT_VOL_NOT_AVAILABLE,
  eventVolCopy,
  EXIT_RULE_NOT_STAMPED,
  exitRuleCopy,
  formatEvPerShare,
  formatImpliedProbabilityPct,
  formatMaxLossPerContract,
  formatMaxProfitLabel,
  formatPerShareUsd,
  formatSpreadPct,
  HAR_RV_NOT_AVAILABLE,
  harRvCopy,
  harRvVrpCopy,
  hasEconomics,
  IMPLIED_PROBABILITY_TOOLTIP,
  OI_WALLS_NOT_AVAILABLE,
  oiWallBarRows,
  oiWallProximityCopy,
  parseDecimalString,
  reasonCodeLabel,
  regimeCopy,
  structureVsAlternativeCopy,
  WHY_NOW_CALL_PUT_RATIO_LOWER_BOUND,
  WHY_NOW_CALL_PUT_RATIO_UPPER_BOUND,
  WHY_NOW_ENERGY_ACTIVITY_MIN,
  WHY_NOW_EVENT_MOVE_TOLERANCE_PT,
  WHY_NOW_IV_HISTORY_HIGH_MIN,
  WHY_NOW_IV_HISTORY_LOW_MAX,
  WHY_NOW_RELATIVE_STRENGTH_EXCESS_MIN,
  WHY_NOW_SQUEEZE_PERCENTILE_MAX,
} from "@/domains/trade-ideas/lib/screenerLabels";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/ui/tooltip";
import { cn } from "@/shared/lib/utils";

// ---------------------------------------------------------------------------
// JS-1104: shared deep-dive composition for the screener drawer, daily
// edition expansion, and ticker-context expansion. The flat micro-label
// register below is the owner-approved card composition; the older exported
// fact components remain here because the register deliberately reuses their
// established copy, materiality gates, and honest-absence behavior.
// ---------------------------------------------------------------------------

export interface QuantifiedWhyNowRow {
  iv_rank?: string | null;
  iv_percentile?: string | null;
  iv_rv_regime?: string | null;
  relative_volume?: {
    available: boolean;
    multiple?: string | null;
    lookback_sessions: number;
    as_of_session?: string | null;
    reason?: string | null;
  } | null;
  iv_rv_read?: {
    atm30_iv?: string | null;
    atm30_iv_absent_reason?: string | null;
    realized_vol_20d?: string | null;
    realized_vol_20d_absent_reason?: string | null;
    term_slope_60d_minus_30d?: string | null;
    risk_reversal_30d_25delta?: string | null;
  } | null;
  relative_strength?: Record<string, unknown> | null;
  options_flow?: Record<string, unknown> | null;
  discovery_lane?: string | null;
  energy_score_snapshot?: {
    composite?: string | null;
    signals_available?: number | null;
    signals?: Record<string, { score?: string | null } | undefined> | null;
  } | null;
}

export interface DeepDiveSetup extends EntryFourQuestionSetup {
  contradictions?: string[];
}

export interface DeepDiveRow extends QuantifiedWhyNowRow {
  rejected_strategy_candidates?: RejectedStrategyCandidate[] | null;
}

// A single-session volume reading below 1.5x is ordinary variation rather
// than a material why-now catalyst; keeping it out prevents the drawer from
// turning routine tape into false urgency.
export const WHY_NOW_RVOL_MULTIPLE_MIN = 1.5;

function decimalField(record: Record<string, unknown> | null | undefined, key: string): number | null {
  const value = record?.[key];
  return typeof value === "string" ? parseDecimalString(value) : null;
}

function integerLike(value: number): string {
  return value.toLocaleString("en-US", { maximumFractionDigits: 1 });
}

function ordinalInteger(value: number): string {
  const mod100 = value % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${value}th`;
  if (value % 10 === 1) return `${value}st`;
  if (value % 10 === 2) return `${value}nd`;
  if (value % 10 === 3) return `${value}rd`;
  return `${value}th`;
}

function signedPercent(value: number): string {
  const pct = value * 100;
  return `${pct > 0 ? "+" : ""}${pct.toFixed(1)}%`;
}

function signedPoints(value: number): string {
  const points = value * 100;
  return `${points > 0 ? "+" : ""}${points.toFixed(1)}pt`;
}

function ivHistoryTone(value: number | null): "Low" | "High" | null {
  if (value === null) return null;
  if (value <= WHY_NOW_IV_HISTORY_LOW_MAX) return "Low";
  if (value >= WHY_NOW_IV_HISTORY_HIGH_MIN) return "High";
  return null;
}

function boundedIvHistoryValue(value: string | null | undefined): number | null {
  const parsed = parseDecimalString(value);
  return parsed !== null && parsed >= 0 && parsed <= 100 ? parsed : null;
}

function withoutTrailingPeriod(value: string): string {
  return value.replace(/\.$/, "");
}

function quantifiedWhyNowFragments(
  row: QuantifiedWhyNowRow,
  entry: MultiStrategyScreenEntry,
): string[] {
  const lines: string[] = [];

  const relativeVolume = row.relative_volume;
  const relativeVolumeMultiple = parseDecimalString(relativeVolume?.multiple);
  if (
    relativeVolume?.available === true &&
    relativeVolumeMultiple !== null &&
    relativeVolumeMultiple >= WHY_NOW_RVOL_MULTIPLE_MIN &&
    Number.isInteger(relativeVolume.lookback_sessions) &&
    relativeVolume.lookback_sessions > 0
  ) {
    lines.push(
      `RVOL ${relativeVolumeMultiple.toFixed(1)}x its ${relativeVolume.lookback_sessions}-day average`,
    );
  }

  const rs = row.relative_strength;
  const nameReturn = decimalField(rs, "name_return");
  const benchmarkReturn = decimalField(rs, "benchmark_return");
  const excessReturn = decimalField(rs, "excess_return");
  const lookback = rs?.lookback_bars;
  if (
    rs?.available !== false &&
    nameReturn !== null &&
    benchmarkReturn !== null &&
    excessReturn !== null &&
    typeof lookback === "number" &&
    Number.isInteger(lookback) &&
    lookback > 0 &&
    Math.abs(excessReturn) >= WHY_NOW_RELATIVE_STRENGTH_EXCESS_MIN
  ) {
    lines.push(
      `${excessReturn > 0 ? "outperforming" : "underperforming"} SPY ${signedPoints(excessReturn)} over ${lookback}d (${signedPercent(nameReturn)} vs ${signedPercent(benchmarkReturn)})`,
    );
  }

  // The producer persists event_vol only after the canonical earnings-window
  // gate clears. Once present, the line always renders; price parity does not
  // suppress a material upcoming event.
  const eventVol = entry.event_vol;
  const impliedMove = parseDecimalString(eventVol?.implied_move?.implied_move_pct);
  const medianMove = parseDecimalString(eventVol?.historical_distribution?.median_abs_move_pct);
  if (impliedMove !== null && medianMove !== null) {
    const history = eventVol?.historical_distribution;
    const ratio = parseDecimalString(eventVol?.implied_to_historical_ratio);
    const displayedImpliedMove = Number(impliedMove.toFixed(1));
    const displayedMedianMove = Number(medianMove.toFixed(1));
    const displayedDifference = displayedImpliedMove - displayedMedianMove;
    const displayedToleranceSteps = Math.round(displayedDifference / WHY_NOW_EVENT_MOVE_TOLERANCE_PT);
    const comparison = displayedToleranceSteps === 0
      ? "in line with"
      : displayedToleranceSteps > 0
        ? "above"
        : "below";
    const evidence = [`±${impliedMove.toFixed(1)}% vs ${medianMove.toFixed(1)}% median`];
    if (ratio !== null) evidence.push(`${ratio.toFixed(2)}×`);
    if (typeof history?.sample_size === "number" && history.sample_size > 0) evidence.push(`${history.sample_size} quarters`);
    lines.push(
      `earnings move ${comparison} history (${evidence.join("; ")})`,
    );
  }

  const flow = row.options_flow;
  const flowRatio = decimalField(flow, "ratio");
  const flowLabel = typeof flow?.label === "string" ? flow.label : null;
  const callVolume = decimalField(flow, "call_volume");
  const putVolume = decimalField(flow, "put_volume");
  const callOi = decimalField(flow, "call_open_interest");
  const putOi = decimalField(flow, "put_open_interest");
  const callHeavy = flowLabel === "CALL_HEAVY"
    ? flowRatio === null || flowRatio > WHY_NOW_CALL_PUT_RATIO_UPPER_BOUND
    : flowLabel === null && flowRatio !== null && flowRatio > WHY_NOW_CALL_PUT_RATIO_UPPER_BOUND;
  const putHeavy = flowLabel === "PUT_HEAVY"
    ? flowRatio === null || flowRatio < WHY_NOW_CALL_PUT_RATIO_LOWER_BOUND
    : flowLabel === null && flowRatio !== null && flowRatio < WHY_NOW_CALL_PUT_RATIO_LOWER_BOUND;
  const zeroDisplayedVolume = callVolume === 0 && putVolume === 0;
  // The legacy screener overlay carries no activity baseline or z-score, so
  // this claim stays ratio-only and never invents "hot versus normal" context.
  if (flow?.available !== false && (callHeavy || putHeavy)) {
    if (zeroDisplayedVolume) {
      if (callOi !== null && putOi !== null) {
        lines.push(`${callHeavy ? "call" : "put"}-heavy positioning (OI ${integerLike(callOi)}/${integerLike(putOi)})`);
      }
    } else if (callVolume !== null && putVolume !== null && callVolume > 0 && putVolume === 0 && callHeavy) {
      lines.push(`one-sided tape — calls only (${integerLike(callVolume)} calls, 0 puts)`);
    } else if (callVolume !== null && putVolume !== null && putVolume > 0 && callVolume === 0 && putHeavy) {
      lines.push(`one-sided tape — puts only (0 calls, ${integerLike(putVolume)} puts)`);
    } else if (flowRatio !== null && flowRatio >= 0) {
      const volumeEvidence = callVolume !== null && putVolume !== null
        ? `; ${integerLike(callVolume)} calls vs ${integerLike(putVolume)} puts`
        : "";
      lines.push(`${callHeavy ? "call" : "put"}-heavy tape (call/put ${flowRatio.toFixed(2)}x${volumeEvidence})`);
    }
  }

  const energyComposite = parseDecimalString(row.energy_score_snapshot?.composite);
  if (
    row.discovery_lane === "ENERGY" &&
    energyComposite !== null &&
    energyComposite >= WHY_NOW_ENERGY_ACTIVITY_MIN &&
    energyComposite <= 1
  ) {
    const rating = Math.round(energyComposite * 100);
    lines.push(`energy activity ${rating}/100 — elevated`);
  }

  const squeezeScore = parseDecimalString(row.energy_score_snapshot?.signals?.squeeze_percentile?.score);
  if (row.discovery_lane === "ENERGY" && squeezeScore !== null && squeezeScore >= 0 && squeezeScore <= 1) {
    const squeezePercentile = Math.round((1 - squeezeScore) * 100);
    if (squeezePercentile <= WHY_NOW_SQUEEZE_PERCENTILE_MAX) {
      lines.push(`squeeze ${ordinalInteger(squeezePercentile)} pctile — coiled`);
    }
  }

  return lines;
}

// JS-1100: claim-first Q1 sentences composed only from persisted row and
// entry facts. Every non-event line clears an exported materiality threshold;
// the producer-gated earnings line renders whenever its complete fact group is
// present. Persisted setup facts (or legacy reason-code chips) always render
// first and remain when narrative evidence is suppressed.
export function QuantifiedWhyNowSentences({
  row,
  entry,
  baseEvidence,
}: {
  row: QuantifiedWhyNowRow;
  entry: MultiStrategyScreenEntry;
  baseEvidence: ReactNode;
}) {
  const lines = quantifiedWhyNowFragments(row, entry);
  return (
    <>
      {baseEvidence}
      {lines.length > 0 && (
        <FactRun testId="quantified-why-now" fragments={lines} className="mt-2" />
      )}
    </>
  );
}

type FactFragment = { text: string; muted?: boolean; warning?: boolean; title?: string };

export function FactRun({
  fragments,
  className,
  testId,
}: {
  fragments: Array<string | FactFragment>;
  className?: string;
  testId?: string;
}) {
  if (fragments.length === 0) return null;
  return (
    <p
      data-testid={testId}
      className={cn("font-mono text-[11px] leading-[1.8] tabular-nums text-foreground", className)}
    >
      {fragments.map((fragment, index) => {
        const item = typeof fragment === "string" ? { text: fragment } : fragment;
        return (
          <span key={`${item.text}-${index}`}>
            {index > 0 && <span aria-hidden className="text-muted-foreground"> · </span>}
            <span
              title={item.title}
              className={cn(item.muted && "text-muted-foreground", item.warning && "text-warning")}
            >
              {item.text}
            </span>
          </span>
        );
      })}
    </p>
  );
}

function lowerFirst(value: string): string {
  return value.length > 0 ? value.charAt(0).toLowerCase() + value.slice(1) : value;
}

function volRegimeFragment(regime: string | null | undefined): string {
  switch (regime) {
    case "IMPLIED_CHEAP": return "implied cheap";
    case "IMPLIED_RICH": return "implied rich";
    case "BALANCED": return "implied in line with realized";
    case "INSUFFICIENT_DATA": return "vol comparison unavailable";
    default: return "vol regime unknown";
  }
}

function volReadFragments(row: QuantifiedWhyNowRow, entry: MultiStrategyScreenEntry): FactFragment[] {
  const fragments: FactFragment[] = [];
  const read = row.iv_rv_read;
  const iv = parseDecimalString(read?.atm30_iv);
  const rv = parseDecimalString(read?.realized_vol_20d);
  if (iv !== null && iv > 0 && rv !== null && rv > 0) {
    fragments.push({
      text: `IV ${(iv * 100).toFixed(1)}% (atm 30d) vs RV ${(rv * 100).toFixed(1)}% (20d) — ${volRegimeFragment(row.iv_rv_regime)}`,
    });
  } else {
    if (iv !== null && iv > 0) fragments.push({ text: `IV ${(iv * 100).toFixed(1)}% (atm 30d)` });
    if (rv !== null && rv > 0) fragments.push({ text: `RV ${(rv * 100).toFixed(1)}% (20d)` });
  }

  const term = parseDecimalString(read?.term_slope_60d_minus_30d);
  if (term !== null) {
    const points = term * 100;
    fragments.push({
      text: `term ${points > 0 ? "+" : ""}${points.toFixed(1)}pt ${points > 0 ? "contango" : points < 0 ? "backwardation" : "flat"}`,
    });
  }

  const rr = parseDecimalString(read?.risk_reversal_30d_25delta);
  if (rr !== null) {
    const points = rr * 100;
    fragments.push({
      text: `RR ${points > 0 ? "+" : ""}${points.toFixed(1)} (${points > 0 ? "calls bid" : points < 0 ? "puts bid" : "balanced"})`,
    });
  }

  const rank = boundedIvHistoryValue(row.iv_rank);
  if (rank !== null) {
    const tone = ivHistoryTone(rank);
    fragments.push({
      text: `IV rank ${integerLike(rank)}/100${tone ? ` — ${tone.toLowerCase()} end of its own 1-yr range` : ""}`,
    });
  }

  // Keep the existing HAR-RV materiality helpers as the authority for
  // whether these model facts are strong enough to enter the register; only
  // their sentence formatting changes here.
  const harRv = harRvCopy(entry.har_rv);
  if (harRv !== null) {
    fragments.push({ text: withoutTrailingPeriod(harRv) });
    const vrp = harRvVrpCopy(entry.har_rv);
    if (vrp) fragments.push({ text: withoutTrailingPeriod(vrp) });
  }

  if (fragments.length === 0) {
    const fallback = regimeCopy(row.iv_rv_regime);
    fragments.push({ text: fallback.label, muted: fallback.muted });
  }
  return fragments;
}

function compactDecimal(value: string | null | undefined): string | null {
  const parsed = parseDecimalString(value);
  if (parsed === null) return null;
  return parsed.toLocaleString("en-US", { maximumFractionDigits: 2 });
}

function pricedLegFragment(leg: NonNullable<MultiStrategyScreenEntry["priced_legs"]>[number]): string | null {
  const strike = compactDecimal(leg.strike);
  const right = leg.option_type === "CALL" ? "C" : leg.option_type === "PUT" ? "P" : null;
  if (!leg.action || strike === null || right === null) return null;
  return `${leg.action.toLowerCase()} ${strike}${right}`;
}

function quoteAgeFragment(entry: MultiStrategyScreenEntry, now: Date): FactFragment | null {
  const quoteTimes = (entry.priced_legs ?? [])
    .map((leg) => leg.quoted_at ? Date.parse(leg.quoted_at) : NaN)
    .filter(Number.isFinite);
  if (quoteTimes.length === 0) return null;
  const ageSeconds = Math.max(0, Math.floor((now.getTime() - Math.min(...quoteTimes)) / 1_000));
  const text = ageSeconds < 60
    ? `quotes ${ageSeconds}s old`
    : ageSeconds < 3_600
      ? `quotes ${Math.floor(ageSeconds / 60)}m old`
      : `quotes ${Math.floor(ageSeconds / 3_600)}h old`;
  return { text, warning: ageSeconds >= 5 * 60 };
}

function breakevenFragment(entry: MultiStrategyScreenEntry): string | null {
  const parsedBreakeven = parseDecimalString(entry.breakevens[0]);
  if (parsedBreakeven === null) return null;
  const breakeven = parsedBreakeven.toFixed(2);
  const distance = parseDecimalString(entry.be_adr_ratio?.breakeven_excess_pct);
  if (distance === null || entry.archetype === "iron_condor") return `breakeven ${breakeven}`;
  const sign = ["long_put", "bear_put_debit", "bull_put_credit"].includes(entry.archetype ?? "") ? "-" : "+";
  return `breakeven ${breakeven} (${sign}${Math.abs(distance).toFixed(1)}%)`;
}

function arithmeticFragments(entry: MultiStrategyScreenEntry, now: Date): FactFragment[] {
  const fragments: FactFragment[] = [];
  for (const leg of entry.priced_legs ?? []) {
    const text = pricedLegFragment(leg);
    if (text) fragments.push({ text });
  }
  const friction = entry.execution_friction;
  const mid = formatPerShareUsd(friction?.net_mid_per_share);
  const fill = formatPerShareUsd(friction?.net_realistic_fill_per_share ?? entry.natural_entry_per_share);
  const spread = formatSpreadPct(friction?.spread_width_pct_of_premium);
  const maxLoss = formatMaxLossPerContract(entry.maximum_loss_usd);
  // JS-1112 round 3 finding 2: an unbounded max profit renders as "uncapped"
  // (trader vernacular) -- never a number, and never the SAME em-dash used
  // for genuinely unavailable data (a bounded structure whose dollar figure
  // just didn't resolve). See formatMaxProfitLabel's own docstring for the
  // three-state disambiguation this drives.
  const maxProfit = formatMaxProfitLabel(
    entry.maximum_profit_usd,
    entry.implied_probability?.max_profit_unbounded,
  );
  const breakeven = breakevenFragment(entry);
  const pop = formatImpliedProbabilityPct(entry.implied_probability?.probability_of_profit)?.replace(/\.0%$/, "%");
  if (mid) fragments.push({ text: `mid ${mid}` });
  if (fill) fragments.push({ text: `fill ${fill}` });
  if (spread) fragments.push({ text: `spread ${spread}` });
  if (maxLoss) fragments.push({ text: `max loss ${maxLoss}` });
  if (maxProfit) fragments.push({ text: `max profit ${maxProfit}` });
  if (breakeven) fragments.push({ text: breakeven });
  if (pop) fragments.push({ text: `PoP* ${pop}`, title: IMPLIED_PROBABILITY_TOOLTIP });
  const quoteAge = quoteAgeFragment(entry, now);
  if (quoteAge) fragments.push(quoteAge);
  return fragments;
}

function rejectionFragment(candidate: RejectedStrategyCandidate): string {
  const fit = parseDecimalString(candidate.fit_score);
  const reason = candidate.rejection_reason_code === "BELOW_MINIMUM_FIT_SCORE"
    ? "below floor"
    : candidate.rejection_reason_code === "REQUIRES_CORROBORATED_RICH"
      ? "requires corroborated rich vol"
      : lowerFirst(reasonCodeLabel(candidate.rejection_reason_code));
  return `${lowerFirst(reasonCodeLabel(candidate.archetype))} considered (fit ${fit === null ? "—" : fit.toFixed(2)}) — ${reason}`;
}

function axisScore(idea: MultiStrategyBestIdea | null | undefined, ...names: string[]): number | null {
  const axis = idea?.axes.find((candidate) => names.includes(candidate.name ?? ""));
  const score = parseDecimalString(axis?.score);
  return score === null ? null : Math.round(Math.max(0, Math.min(1, score)) * 100);
}

function confidenceScore(idea: MultiStrategyBestIdea | null | undefined): number | null {
  const confidence = parseDecimalString(idea?.confidence);
  return confidence === null ? null : Math.round(Math.max(0, Math.min(1, confidence)) * 100);
}

export function SignalBreakdownDisclosure({ idea }: { idea?: MultiStrategyBestIdea | null }) {
  const confidence = confidenceScore(idea);
  const setup = axisScore(idea, "DIRECTIONAL_SETUP", "SETUP");
  const confirmationAxis = idea?.axes.find((axis) => ["CORROBORATION", "CONFIRMATION"].includes(axis.name ?? ""));
  const confirmation = axisScore(idea, "CORROBORATION", "CONFIRMATION");
  const risk = axisScore(idea, "RISK", "RISK_LIQUIDITY");
  const tally = confirmationAxis?.confirmation_vote_tally;
  const summary: string[] = [];
  if (confidence !== null) summary.push(`confidence ${confidence}`);
  if (setup !== null) summary.push(`setup ${setup}`);
  if (confirmation !== null) {
    summary.push(`confirmation ${confirmation}${tally ? ` (${tally.agree_count}/${tally.available_count} agreed)` : ""}`);
  }
  if (risk !== null) summary.push(`risk ${risk}`);

  const reasons = idea?.axes.flatMap((axis) => axis.reasons.map((reason) => lowerFirst(axisReasonCodeLabel(reason)))) ?? [];
  const liquidity = idea?.axes.map((axis) => axis.liquidity_gate).find(Boolean);
  const detailFragments: FactFragment[] = [];
  const worstSpread = parseDecimalString(liquidity?.worst_spread);
  if (worstSpread !== null) detailFragments.push({ text: `worst leg spread ${(worstSpread * 100).toFixed(0)}% of mid` });
  if (liquidity?.min_oi != null) detailFragments.push({ text: `min OI ${liquidity.min_oi.toLocaleString("en-US")}` });
  detailFragments.push(...reasons.map((text) => ({ text, muted: true })));

  return (
    <details className="group border-t border-border pt-2" data-testid="signal-breakdown">
      <summary className="cursor-pointer list-none font-mono text-[11px] leading-[1.8] tabular-nums text-muted-foreground marker:content-none">
        {summary.length > 0 ? summary.join(" · ") : "signal breakdown unavailable"}
      </summary>
      <FactRun fragments={detailFragments} className="mt-1" />
    </details>
  );
}

export function DeepDiveSection({ title, last = false, children }: { title: string; last?: boolean; children: ReactNode }) {
  return (
    <section className={cn(!last && "border-b border-border pb-3")}>
      <h4 className="mb-1 font-mono text-[9px] font-semibold uppercase tracking-[0.09em] text-muted-foreground">
        {title}
      </h4>
      <div>{children}</div>
    </section>
  );
}

export function IdeaDeepDiveSections({
  entry,
  row = {},
  reasonCodes,
  setup,
  watchOutCodes = [],
  abstainHeadline,
  idea,
  now = new Date(),
  includeBeAdr = false,
}: {
  entry: MultiStrategyScreenEntry;
  row?: DeepDiveRow;
  reasonCodes: string[];
  setup?: DeepDiveSetup | null;
  watchOutCodes?: string[];
  abstainHeadline: string;
  idea?: MultiStrategyBestIdea | null;
  now?: Date;
  includeBeAdr?: boolean;
}) {
  const economicsReady = hasEconomics(entry);
  const quantified = quantifiedWhyNowFragments(row, entry);
  const rvol = quantified.filter((fragment) => fragment.startsWith("RVOL "));
  const materialFacts = quantified.filter((fragment) => !fragment.startsWith("RVOL "));
  const screenFacts = reasonCodes
    .filter((code) => !["IMPLIED_CHEAP", "IMPLIED_RICH", "IMPLIED_BALANCED", "IV_RANK_UNKNOWN"].includes(code))
    .map((code) => lowerFirst(reasonCodeLabel(code)));
  const whyNow = setup
    ? [...setup.why_now.map(withoutTrailingPeriod), ...quantified]
    : [...rvol, ...screenFacts, ...materialFacts];
  // DESK-009 (JS-1144), Codex adversarial-review HIGH (round 3): the
  // row-face watch-outs render whether or not a setup is bound.
  //
  // RCA: this line used to pick ONE source -- `setup.contradictions` when a
  // setup existed, `watchOutCodes` otherwise. Round 2 wired both call sites
  // to pass the selected entry's disclosures as `watchOutCodes`, but every
  // bound-setup row took the other branch, so negative EV, the cheap-vol
  // waiver, the rich-vol escape and an unresolved regime stayed invisible
  // on exactly the rows that have a thesis -- the ones a trader reads most.
  //
  // The two are different KINDS of caveat (a setup contradiction is the
  // thesis arguing against itself; a disclosure is the desk telling you how
  // this structure got published), so neither supersedes the other: both
  // are merged, de-duplicated on the rendered text so a fact stated by both
  // sources is stated once.
  const contradictions = Array.from(
    new Set([
      ...(setup?.contradictions ?? []).map(withoutTrailingPeriod),
      ...watchOutCodes.map((code) => withoutTrailingPeriod(reasonCodeLabel(code))),
    ]),
  );
  const hasCarry = carryBreakevenCopy(entry.carry_breakeven) != null;
  const hasScenario = (entry.scenario_grid?.cells.length ?? 0) > 0;
  const hasCondor = Boolean(entry.be_adr_condor_wings?.put_side || entry.be_adr_condor_wings?.call_side);
  const hasBeAdr = includeBeAdr && beAdrRowView(entry.be_adr_ratio) != null;
  const hasQ2 = hasCarry || hasScenario || hasCondor || hasBeAdr;
  const abstain = entry.abstain_reason_codes ?? [];
  const structureFragments: FactFragment[] = [];
  const structureCopy = structureVsAlternativeCopy(entry.archetype);
  if (structureCopy) structureFragments.push({ text: withoutTrailingPeriod(structureCopy) });
  for (const candidate of row.rejected_strategy_candidates ?? []) {
    structureFragments.push({ text: rejectionFragment(candidate) });
  }

  return (
    <div className="space-y-3" data-testid="idea-deep-dive-sections">
      <DeepDiveSection title="WHY NOW">
        {whyNow.length > 0 ? (
          <FactRun fragments={whyNow} />
        ) : (
          <p className="font-mono text-[11px] italic text-muted-foreground">No why-now evidence published.</p>
        )}
        {setup && (
          <FactRun
            className="mt-1"
            fragments={[{
              text: `but: ${contradictions.length > 0 ? contradictions.join(" · ") : "no contradictions published"}`,
              muted: contradictions.length === 0,
              warning: contradictions.length > 0,
            }]}
          />
        )}
        {!setup && contradictions.length > 0 && (
          <FactRun className="mt-1" fragments={[{ text: `but: ${contradictions.join(" · ")}`, warning: true }]} />
        )}
        {!setup && (
          <p className="mt-1 font-mono text-[9px] text-muted-foreground">
            screen-level evidence (no bound setup thesis)
          </p>
        )}
      </DeepDiveSection>

      <DeepDiveSection title="VOL READ">
        <FactRun fragments={volReadFragments(row, entry)} />
      </DeepDiveSection>

      <DeepDiveSection title="ARITHMETIC">
        {economicsReady ? (
          <FactRun fragments={arithmeticFragments(entry, now)} />
        ) : (
          <FactRun fragments={[abstainHeadline, ...abstain.slice(1).map((code) => reasonCodeLabel(code))]} />
        )}
      </DeepDiveSection>

      {economicsReady && (
        <DeepDiveSection title="WHAT HAS TO HAPPEN">
          {hasQ2 ? (
            <dl>
              {includeBeAdr && <BeAdrFact entry={entry} />}
              <CarryBreakevenBar entry={entry} />
              <ScenarioGridTable entry={entry} />
              <CondorWingsNote entry={entry} />
            </dl>
          ) : (
            <p className="font-mono text-[11px] italic text-muted-foreground">
              Carry and scenario analysis not available for this idea.
            </p>
          )}
        </DeepDiveSection>
      )}

      <DeepDiveSection title="STRUCTURE">
        {structureFragments.length > 0 ? (
          <FactRun fragments={structureFragments} />
        ) : (
          <p className="font-mono text-[11px] italic text-muted-foreground">Structure comparison not available.</p>
        )}
      </DeepDiveSection>

      <DeepDiveSection title="EXIT">
        <p className={cn("font-mono text-[11px] leading-[1.8]", entry.exit_rule ? "text-foreground" : "italic text-muted-foreground")}>
          {exitRuleCopy(entry.exit_rule) ?? EXIT_RULE_NOT_STAMPED}
        </p>
      </DeepDiveSection>

      <DeepDiveSection title="SIGNAL BREAKDOWN" last>
        <SignalBreakdownDisclosure idea={idea} />
      </DeepDiveSection>
    </div>
  );
}

// ---------------------------------------------------------------------------

export function EconomicsRow({ term, value }: { term: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="font-mono text-[9px] font-semibold uppercase tracking-[0.09em] text-muted-foreground">{term}</dt>
      <dd className="font-mono text-[12px] tabular-nums text-foreground">{value}</dd>
    </div>
  );
}

// Implied probability-of-profit + EV/share (JS-1052): a MODEL-IMPLIED
// risk-neutral estimate, never a forecast or a win-rate claim -- the tooltip
// carries the exact required honesty framing. Honestly absent when the entry
// lacks a real per-leg implied volatility for every leg.
// a11y: the disclosure trigger is keyboard-focusable (tabIndex 0) so
// keyboard users can reach the "not a forecast / not a win rate" tooltip via
// Radix's focus-triggered open, not just pointer hover.
export function ImpliedProbabilityRow({ entry }: { entry: MultiStrategyScreenEntry }) {
  const probability = entry.implied_probability;
  if (!probability) return null;
  const popLabel = formatImpliedProbabilityPct(probability.probability_of_profit);
  const evLabel = formatEvPerShare(probability.expected_value_per_share);
  if (popLabel == null && evLabel == null) return null;
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              tabIndex={0}
              role="note"
              aria-label={`Implied PoP: ${IMPLIED_PROBABILITY_TOOLTIP}`}
              className="cursor-help underline decoration-dotted underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
            >
              Implied PoP
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-[260px]">{IMPLIED_PROBABILITY_TOOLTIP}</TooltipContent>
        </Tooltip>
      </dt>
      <dd className="font-mono text-[12px] tabular-nums text-foreground">
        {popLabel ?? "—"}
        {evLabel != null && <span className="ml-1.5 text-muted-foreground">EV {evLabel}/sh</span>}
      </dd>
    </div>
  );
}

// JS-1076 (T1b) item 1: the JS-1053 mechanical exit rule stamped at
// generation time, rendered as one deterministic sentence
// (screenerLabels.exitRuleCopy). Honest absence: an entry the exit-policy
// stamping never ran over (e.g. an unpriced/shadow entry, or a pre-JS-1053
// row) shows the "not stamped" note instead -- never a fabricated rule.
export function ExitRuleNote({ entry }: { entry: MultiStrategyScreenEntry }) {
  const copy = exitRuleCopy(entry.exit_rule);
  return (
    <div className="mt-2 border-t border-border pt-2">
      <dt className="mb-1 text-[11px] uppercase tracking-wide text-muted-foreground">Exit rule</dt>
      <dd className={cn("text-[11px] leading-relaxed", copy ? "text-foreground" : "italic text-muted-foreground")}>
        {copy ?? EXIT_RULE_NOT_STAMPED}
      </dd>
    </div>
  );
}

// JS-1076 (T1b) item 4: table-driven structure-vs-alternative copy, keyed off
// the displayed entry's archetype (screenerLabels.structureVsAlternativeCopy).
// An unmapped/absent archetype renders nothing (honest fallback).
export function StructureVsAlternativeNote({ entry }: { entry: MultiStrategyScreenEntry }) {
  const copy = structureVsAlternativeCopy(entry.archetype);
  if (!copy) return null;
  return <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground">{copy}</p>;
}

// JS-1076 (T1b) item 5a: the JS-1075 carry-breakeven bar -- required daily
// move vs. expected daily move, with the ratio, or the THETA_COLLECTS /
// DECAY_DOMINATES / GAMMA_FLAT honest state copy (screenerLabels.carryBreakevenCopy
// covers every state, including the retro FIX 2 adverse-move-ceiling sentence
// on THETA_COLLECTS). Renders nothing when the group is absent (no real
// priced legs / no per-leg IV) -- never a fabricated ratio. The fill bar
// itself is exclusive to MOVE_REQUIRED (the only state with both a required
// AND an expected move to compare).
export function CarryBreakevenBar({ entry }: { entry: MultiStrategyScreenEntry }) {
  const carry = entry.carry_breakeven;
  const copy = carryBreakevenCopy(carry);
  if (!copy) return null;
  const required = parseDecimalString(carry?.required_daily_move_usd);
  const expected = parseDecimalString(carry?.expected_daily_move_usd);
  // Adversarial-review fix: `required > 0` is required too, not just
  // `expected > 0` -- a `required_daily_move_usd` of "0" would otherwise let
  // fillPct compute as Infinity (clamped to 100), fabricating a fully-filled
  // bar for a structure with no real required move.
  const showBar =
    carry?.state === "MOVE_REQUIRED" && required !== null && required > 0 && expected !== null && expected > 0;
  const fillPct = showBar ? Math.max(0, Math.min(100, (expected / required) * 100)) : null;
  return (
    <div className="mt-2 border-t border-border pt-2">
      <dt className="mb-1 text-[11px] uppercase tracking-wide text-muted-foreground">Carry breakeven</dt>
      <dd className="text-[11px] leading-relaxed text-foreground">
        {copy}
        {fillPct != null && (
          <>
            {/* JS-1096 (wireframe parity): the prototype's `.carry-bar-track`
                keeps a fixed tick at the 100% mark (the required move
                itself) alongside the fill (the typical/expected day as a
                share of that requirement) -- so the bar always shows BOTH
                "how full" and "where the threshold sits", even when the
                fill is clamped at 100% for a ratio past 1. */}
            <span className="relative mt-1.5 block h-1.5 w-24 overflow-hidden rounded-full bg-border" aria-hidden>
              <span className="block h-full rounded-full bg-primary" style={{ width: `${fillPct}%` }} />
              <span className="absolute inset-y-0 right-0 w-px bg-foreground" />
            </span>
            <span className="mt-1 block font-mono text-[9px] normal-case text-muted-foreground">
              bar = typical day as % of required move · tick = required move (100%)
            </span>
          </>
        )}
      </dd>
    </div>
  );
}

// JS-1089 round 2 (FIX 1): the class-aware BE×ADR / cushion fact
// (screenerLabels.beAdrRowView) -- on the screener this is a ROW-level cell
// (MultiStrategyScreenTable's ScreenRow renders it directly from `display`,
// never inside ThesisDrawer, since the row owns it post-JS-1088). The daily
// edition has no separate row/drawer split -- the card's expansion IS the
// whole surface -- so this component renders the SAME fact (identical
// beAdrRowView dispatch, identical BE_ADR_TONE_CLASS tone map, identical
// non-color word/qualifier) inside the argument itself rather than dropping
// it. Honest absence (renders nothing) when be_adr_ratio itself is absent,
// matching the row's own "—" treatment for a fully-priced entry that still
// lacks this fact (no cached OHLCV series, a stale one, a degenerate
// zero-ADR series, or a topology mismatch).
export function BeAdrFact({ entry }: { entry: MultiStrategyScreenEntry }) {
  const view = beAdrRowView(entry.be_adr_ratio);
  if (!view) return null;
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">Breakeven × ADR</dt>
      <dd
        className={cn("inline-flex flex-wrap items-center gap-1 font-mono text-[12px] tabular-nums", BE_ADR_TONE_CLASS[view.tone])}
        aria-label={[view.label, view.word, view.qualifier].filter(Boolean).join(", ")}
      >
        {view.label}
        {view.word && <span className="font-sans text-[10px] normal-case text-inherit">{view.word}</span>}
        {view.qualifier && (
          <span className="font-sans text-[10px] normal-case text-muted-foreground">({view.qualifier})</span>
        )}
      </dd>
    </div>
  );
}

// JS-1088 (round 2 FIX 1): the condor's OWN "what has to happen" fact --
// both wing cushions, not just the row's single binding-wing summary. The
// row's BE×ADR cell already shows the binding (smaller, more dangerous)
// wing with a "(near wing)" qualifier; this note gives the FULL picture --
// both wings side by side, so a reader can see how much room the position
// has on BOTH sides, not just the one that currently binds. Renders nothing
// when the group is absent (no series, no resolvable topology, or a
// non-condor entry) -- never a fabricated wing.
export function CondorWingsNote({ entry }: { entry: MultiStrategyScreenEntry }) {
  const wings = entry.be_adr_condor_wings;
  if (!wings) return null;
  const putView = beAdrRowView(wings.put_side);
  const callView = beAdrRowView(wings.call_side);
  if (!putView && !callView) return null;
  return (
    <div className="mt-2 border-t border-border pt-2">
      <dt className="mb-1 text-[11px] uppercase tracking-wide text-muted-foreground">Breakeven cushions (both wings)</dt>
      <dd className="space-y-1 text-[11px] leading-relaxed text-foreground">
        {putView && (
          <div className="flex items-center justify-between gap-3">
            <span className="text-muted-foreground">Put side</span>
            <span className={cn("font-mono tabular-nums", BE_ADR_TONE_CLASS[putView.tone])}>
              {putView.label}
              {putView.word && <span className="ml-1 font-sans normal-case text-inherit">{putView.word}</span>}
              {wings.put_side?.is_binding_wing && <span className="ml-1 font-sans normal-case text-muted-foreground">(near wing)</span>}
            </span>
          </div>
        )}
        {callView && (
          <div className="flex items-center justify-between gap-3">
            <span className="text-muted-foreground">Call side</span>
            <span className={cn("font-mono tabular-nums", BE_ADR_TONE_CLASS[callView.tone])}>
              {callView.label}
              {callView.word && <span className="ml-1 font-sans normal-case text-inherit">{callView.word}</span>}
              {wings.call_side?.is_binding_wing && <span className="ml-1 font-sans normal-case text-muted-foreground">(near wing)</span>}
            </span>
          </div>
        )}
      </dd>
    </div>
  );
}

// JS-1076 (T1b) item 5b: the JS-1075 scenario-grid mini-table -- spot columns
// x vol rows, one table per time slice (each labeled by its own
// days_to_expiry). Renders nothing when the group is absent; a
// `dropped_cells > 0` note surfaces the honest reason when the backend
// dropped a shocked-spot cell rather than fabricating one.
const TIME_SLICE_LABELS: Record<string, string> = {
  HALF_LIFE_TO_DTE_FLOOR: "Halfway to DTE floor",
  AT_DTE_FLOOR: "At DTE floor",
  // Retro adversarial-review FIX 4 (scenario_grid.v2): the floor slice
  // degraded to latest-valid because the entry is already at/inside the
  // 21-DTE floor -- never labeled "At DTE floor" (that DTE was never
  // actually reached).
  NEAR_EXPIRY_LATEST_VALID: "Near expiry (inside 21-DTE window)",
};

// JS-1096 (wireframe parity): the prototype's compact color-graded matrix
// (desk-hifi/prototype.html's `.scenario-grid` / `.scenario-cell`) grades
// each cell's BACKGROUND by sign, not just its text color -- a stronger,
// faster-scanning red-to-green read than text color alone, while every cell
// still carries the signed dollar figure as a non-color-only signal (a11y
// parity with the row-level threshold treatment elsewhere on this table).
// `pnlToneClass`/`pnlBgClass` are pure presentation over the SAME
// `pnl_usd` fact -- no new data, no changed thresholds (sign-only, exactly
// what the prototype's red/green split already used).
function pnlToneClass(pnl: string | null | undefined): string {
  const parsed = parseDecimalString(pnl);
  if (parsed === null) return "text-muted-foreground";
  if (parsed > 0) return "text-gain";
  if (parsed < 0) return "text-loss";
  return "text-foreground";
}

function pnlBgClass(pnl: string | null | undefined): string {
  const parsed = parseDecimalString(pnl);
  if (parsed === null) return "";
  if (parsed > 0) return "bg-gain/[0.14]";
  if (parsed < 0) return "bg-loss/[0.12]";
  return "";
}

// Signed dollar text: `+$95` / `-$180` / `$0` -- matches the prototype's own
// `fmtUsd` convention EXACTLY (sign, then "$", then the unsigned magnitude --
// never `$-180`, which the prototype never renders either). Parsed through
// the shared decimal gate so a malformed `pnl_usd` renders the honest dash,
// never a fabricated figure.
function fmtSignedUsd(pnl: string | null | undefined): string {
  const parsed = parseDecimalString(pnl);
  if (parsed === null) return "—";
  const sign = parsed < 0 ? "-" : parsed > 0 ? "+" : "";
  const magnitude = pnl!.replace(/^-/, "");
  return `${sign}$${magnitude}`;
}

// Spot-shock column label: the backend's `spot_sigma_multiple` (standard-
// deviation multiples of the time-slice-scaled expected move -- see
// carry_scenario.py's `_SPOT_SIGMA_MULTIPLES`, which is 5 columns
// -2/-1/0/+1/+2, not just the prototype fixture's 3) is the SAME axis the
// prototype labels "-1σ / flat / +1σ"; this generalizes that label to
// however many sigma columns the real grid actually has, never truncating
// real columns to match the fixture's simplified 3-column example. The
// underlying dollar spot price (`spot`) is kept as a title tooltip for the
// reader who wants the literal price, not just the sigma multiple.
function spotSigmaLabel(sigma: string | null | undefined): string {
  const parsed = parseDecimalString(sigma);
  if (parsed === null) return "—";
  if (parsed === 0) return "flat";
  return `${parsed > 0 ? "+" : ""}${parsed}σ`;
}

// JS-1096 round 2 (Codex review fix -- MED finding 2): `spot_sigma_multiple`
// is nullish in the contract (multiStrategyScenarioCellShape), so a slice
// can have cells with a valid spot/vol_shock/pnl_usd but no sigma value at
// all -- keying columns exclusively off sigma silently DROPPED those cells
// from the rendered grid without ever touching `dropped_cells` (an honesty
// gap: the grid would understate itself with no note explaining why). The
// fallback column label, used for the WHOLE slice whenever even one cell in
// it lacks a sigma multiple (see the "every cell has sigma" gate in
// ScenarioGridSlice below -- never a MIXED axis, some columns sigma-labeled
// and others dollar-labeled in the same table): the literal dollar spot
// price this cell was re-priced at.
function spotDollarLabel(spot: string | null | undefined): string {
  const parsed = parseDecimalString(spot);
  if (parsed === null) return "—";
  return `$${spot}`;
}

// Vol-shock row label: the backend's `vol_shock` is a flat IV multiplier
// (0.8 / 1.0 / 1.2 -- carry_scenario.py's `_VOL_SHOCKS`), the SAME axis the
// prototype labels "IV-20% / IV flat / IV+20%"; this derives that same
// label from the multiplier rather than hardcoding the three fixture
// values, so it stays correct if the multiplier set ever changes.
function volShockLabel(volShock: string | null | undefined): string {
  const parsed = parseDecimalString(volShock);
  if (parsed === null) return "—";
  if (parsed === 1) return "IV flat";
  const pct = Math.round((parsed - 1) * 100);
  return `IV${pct > 0 ? "+" : ""}${pct}%`;
}

function ScenarioGridSlice({
  timeSlice,
  cells,
}: {
  timeSlice: string;
  cells: MultiStrategyScenarioCell[];
}) {
  const daysToExpiry = cells.find((cell) => cell.days_to_expiry != null)?.days_to_expiry ?? null;

  // JS-1096 round 2 (Codex review fix -- MED finding 2): `spot_sigma_multiple`
  // is nullish in the contract -- a cell can be fully priced (real spot,
  // vol_shock, pnl_usd) yet still lack it. Keying columns exclusively off
  // sigma would silently drop such cells from the table with no note (a
  // real understatement of the grid, distinct from an honest
  // `dropped_cells` drop). The fix: use the sigma axis ONLY when EVERY cell
  // in this slice has one; otherwise fall back to the literal spot-price
  // axis for the WHOLE slice -- never a mixed axis where some columns are
  // sigma-labeled and others dollar-labeled in the same table, and never a
  // per-cell decision that could silently omit a column's worth of cells.
  const allCellsHaveSigma = cells.length > 0 && cells.every((cell) => cell.spot_sigma_multiple != null);
  const spotKey = allCellsHaveSigma
    ? (cell: MultiStrategyScenarioCell) => cell.spot_sigma_multiple
    : (cell: MultiStrategyScenarioCell) => cell.spot;
  const spotLabel = allCellsHaveSigma ? spotSigmaLabel : spotDollarLabel;

  // Sorted ascending by their own numeric value (never insertion order) so
  // spot columns read left-to-right low-to-high and vol rows read top-to-
  // bottom low-to-high, matching the prototype's fixed IV-20%/flat/IV+20%
  // row order and -σ...+σ column order.
  const spots = [
    ...new Set(cells.map(spotKey).filter((value): value is string => value != null)),
  ].sort((a, b) => (parseDecimalString(a) ?? 0) - (parseDecimalString(b) ?? 0));
  const vols = [
    ...new Set(cells.map((cell) => cell.vol_shock).filter((value): value is string => value != null)),
  ].sort((a, b) => (parseDecimalString(a) ?? 0) - (parseDecimalString(b) ?? 0));
  if (spots.length === 0 || vols.length === 0) return null;
  const cellAt = (spotColumn: string, vol: string) => cells.find((cell) => spotKey(cell) === spotColumn && cell.vol_shock === vol);
  return (
    <div className="min-w-0">
      <p className="mb-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        {TIME_SLICE_LABELS[timeSlice] ?? timeSlice}
        {daysToExpiry != null && <span className="ml-1 normal-case text-inherit">· {daysToExpiry} DTE</span>}
      </p>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-[10px]">
          <thead>
            <tr>
              <th className="px-1.5 py-1 text-left font-normal text-muted-foreground">Vol \ Spot</th>
              {spots.map((spotColumn) => {
                // The literal dollar spot price, kept as a title tooltip
                // whenever the sigma axis is in use (so the reader can still
                // see the underlying price on hover); redundant with the
                // header text itself in the fallback (dollar-axis) case, so
                // omitted there.
                const spotPrice = allCellsHaveSigma
                  ? (cells.find((cell) => cell.spot_sigma_multiple === spotColumn)?.spot ?? undefined)
                  : undefined;
                return (
                  <th
                    key={spotColumn}
                    className="px-1.5 py-1 text-right font-mono font-normal tabular-nums text-muted-foreground"
                    title={spotPrice != null ? `Spot $${spotPrice}` : undefined}
                  >
                    {spotLabel(spotColumn)}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {vols.map((vol) => (
              <tr key={vol} className="border-t border-border">
                <td className="px-1.5 py-1 font-mono tabular-nums text-muted-foreground">{volShockLabel(vol)}</td>
                {spots.map((spotColumn) => {
                  const cell = cellAt(spotColumn, vol);
                  return (
                    <td
                      key={spotColumn}
                      className={cn(
                        "rounded-sm px-1.5 py-1 text-right font-mono tabular-nums",
                        pnlToneClass(cell?.pnl_usd),
                        pnlBgClass(cell?.pnl_usd),
                      )}
                    >
                      {fmtSignedUsd(cell?.pnl_usd)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function ScenarioGridTable({ entry }: { entry: MultiStrategyScreenEntry }) {
  const grid = entry.scenario_grid;
  if (!grid || grid.cells.length === 0) return null;
  const slices = [
    ...new Set(
      grid.cells
        .map((cell) => cell.time_slice)
        .filter((value): value is NonNullable<MultiStrategyScenarioCell["time_slice"]> => value != null),
    ),
  ];
  // Honest single-slice handling: near the 21-DTE floor the two time slices
  // can collapse to one (see carry_scenario.py) -- render whatever slices
  // are actually present, never a fabricated second slice.
  return (
    <div className="mt-2 border-t border-border pt-2">
      <dt className="mb-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">Scenario grid</dt>
      <div className={cn("grid gap-3", slices.length > 1 ? "sm:grid-cols-2" : "grid-cols-1")}>
        {slices.map((slice) => (
          <ScenarioGridSlice
            key={slice}
            timeSlice={slice}
            cells={grid.cells.filter((cell) => cell.time_slice === slice)}
          />
        ))}
      </div>
      {grid.dropped_cells != null && grid.dropped_cells > 0 && (
        <p className="mt-1.5 text-[10px] text-muted-foreground">
          {grid.dropped_cells} scenario {grid.dropped_cells === 1 ? "cell" : "cells"} omitted
          {grid.dropped_reason === "SHOCKED_SPOT_NON_POSITIVE" ? " (a shocked spot fell to zero or below)" : ""}.
        </p>
      )}
    </div>
  );
}

// JS-1077 (T2a): the implied event move vs historical earnings-move
// distribution, and the event-window x exit-clock fact -- rendered together
// since both are catalyst-window-only facts about the SAME upcoming print.
// Both groups are absent on any entry outside the catalyst window (never
// fabricated) -- this note renders nothing at all in that case, matching
// CarryBreakevenBar/ScenarioGridTable's own "absent group -> render nothing"
// convention.
export function EventVolNote({ entry }: { entry: MultiStrategyScreenEntry }) {
  const eventVolText = eventVolCopy(entry.event_vol);
  const exitClockText = earningsExitClockCopy(entry.earnings_exit_clock);
  if (!eventVolText && !exitClockText) return null;
  return (
    <div className="mt-2 border-t border-border pt-2">
      <dt className="mb-1 text-[11px] uppercase tracking-wide text-muted-foreground">Earnings event</dt>
      <dd className="space-y-0.5 text-[11px] leading-relaxed">
        <p className={eventVolText ? "text-foreground" : "italic text-muted-foreground"}>
          {eventVolText ?? EVENT_VOL_NOT_AVAILABLE}
        </p>
        {exitClockText && <p className="text-muted-foreground">{exitClockText}</p>}
      </dd>
    </div>
  );
}

// JS-1079 (T2c): the HAR-RV realized-vol forecast vs implied (variance risk
// premium v1) note. Sits alongside EventVolNote in the drawer's vol-read
// area -- both are model-context notes about the name's own vol pricing.
// ADR-009/011: labeled as a MODEL ESTIMATE with method + fit stats disclosed
// (screenerLabels.harRvCopy's "HAR-RV, R² x, n=y" register) -- never a
// probability, never advice, never "cheap"/"rich"/"overpriced" language.
// Renders the honest HAR_RV_NOT_AVAILABLE note when the kernel tried and
// could not resolve a forecast (`har_rv_absent_reason` present); renders
// NOTHING when the group was never attempted at all (no cached OHLCV series
// for this name) -- matching EventVolNote/OiWallStrip's "one place states
// the absence, distinct from never-attempted" convention.
export function HarRvNote({ entry }: { entry: MultiStrategyScreenEntry }) {
  const harRv = entry.har_rv;
  const absentReason = entry.har_rv_absent_reason;
  if (!harRv && !absentReason) return null;
  const harRvText = harRvCopy(harRv);
  const harRvVrpText = harRvVrpCopy(harRv);
  if (harRv && !harRvText) return null;
  return (
    <div className="mt-2 border-t border-border pt-2">
      <dt className="mb-1 text-[11px] uppercase tracking-wide text-muted-foreground">
        Realized-vol model estimate
      </dt>
      <dd className="text-[11px] leading-relaxed">
        <p className={harRvText ? "text-foreground" : "italic text-muted-foreground"}>
          {harRvText ?? HAR_RV_NOT_AVAILABLE}
        </p>
        {harRvVrpText && <p className="mt-0.5 text-foreground">{harRvVrpText}</p>}
      </dd>
    </div>
  );
}

// JS-1078 (T2b): per-leg wall-proximity note(s) -- one sentence per leg whose
// own strike sits on or one listed strike from a near-tenor OI wall (see
// screenerLabels.oiWallProximityCopy). Walls are OI-CONCENTRATION FACTS,
// never a dealer-direction claim (ADR-011) -- this note states only that
// fact. Renders nothing when `oi_wall_proximity_by_contract` is absent or no
// leg is near a wall (an entry CAN have real OI-wall data with every leg
// honestly not near one -- that is not an absence, just a quiet chain, so
// this note stays silent rather than rendering a "not near" line for every
// leg). The distinct honest-ABSENCE case (the kernel tried and could not
// resolve OI walls at all, `oi_walls_absent_reason` present) is surfaced by
// `OiWallStrip` below instead, matching `EventVolNote`'s "one place states
// the absence" convention.
export function OiWallProximityNotes({ entry }: { entry: MultiStrategyScreenEntry }) {
  const proximityByContract = entry.oi_wall_proximity_by_contract;
  if (!proximityByContract) return null;
  const lines = Object.entries(proximityByContract)
    .map(([contract, proximity], index) => {
      const strike = parseDecimalString(proximity.leg_strike);
      const label = strike !== null ? `Strike ${strike}` : `Leg ${index + 1}`;
      return { key: contract, copy: oiWallProximityCopy(label, proximity) };
    })
    .filter((row): row is { key: string; copy: string } => row.copy != null);
  if (lines.length === 0) return null;
  return (
    <div className="mt-2 border-t border-border pt-2">
      <dt className="mb-1 text-[11px] uppercase tracking-wide text-muted-foreground">Strike positioning</dt>
      <dd className="space-y-0.5 text-[11px] leading-relaxed text-foreground">
        {lines.map((line) => (
          <p key={line.key}>{line.copy}</p>
        ))}
      </dd>
    </div>
  );
}

// JS-1078 (T2b): a compact wall-bars strip -- one bar per top-K near-tenor
// strike, ordered ascending by strike, with wall strikes visually
// distinguished (tone only, never the SOLE signal -- see the "wall" text
// badge for the non-color signifier). Renders the honest
// `oi_walls_absent_reason` note when the kernel tried and could not resolve
// walls at all; renders NOTHING (not even a placeholder) when the group was
// never attempted (no raw chain data for this idea). Facts only (ADR-011):
// this strip shows OI CONCENTRATION, never a dealer-direction claim -- no
// arrows, no "support/resistance" framing, just strike + OI bars.
export function OiWallStrip({ entry }: { entry: MultiStrategyScreenEntry }) {
  const walls = entry.oi_walls;
  const absentReason = entry.oi_walls_absent_reason;
  if (!walls && !absentReason) return null;
  const rows = oiWallBarRows(walls);
  const maxOi = Math.max(1, ...rows.map((row) => row.openInterest));
  return (
    <div className="mt-2 border-t border-border pt-2">
      <dt className="mb-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">OI walls (near tenor)</dt>
      {rows.length > 0 ? (
        <div className="space-y-1">
          {rows.map((row) => (
            <div key={row.strike} className="flex items-center gap-2">
              <span className="w-14 shrink-0 font-mono text-[10px] tabular-nums text-muted-foreground">
                {row.strike}
              </span>
              <span className="h-2.5 flex-1 overflow-hidden rounded-sm bg-border">
                <span
                  className={cn("block h-full rounded-sm", row.isWall ? "bg-primary" : "bg-muted-foreground/40")}
                  style={{
                    width: `${Math.max(4, Math.min(100, (row.openInterest / maxOi) * 100))}%`,
                  }}
                />
              </span>
              <span className="w-14 shrink-0 text-right font-mono text-[10px] tabular-nums text-foreground">
                {row.openInterestLabel}
              </span>
              {row.isWall && (
                <span className="shrink-0 rounded-full border border-border px-1.5 py-0 text-[9px] uppercase tracking-wide text-muted-foreground">
                  wall
                </span>
              )}
            </div>
          ))}
          <p className="pt-0.5 text-[10px] italic text-muted-foreground">
            Walls are OI facts, not dealer-direction claims.
          </p>
        </div>
      ) : (
        <p className="text-[11px] italic text-muted-foreground">{OI_WALLS_NOT_AVAILABLE}</p>
      )}
    </div>
  );
}

// JS-1086 (hi-fi Desk): section chrome for the drawer's four-question frame
// (design-spec.md §4) -- a numbered circular badge + uppercase title, mirroring
// the prototype's `.q-num`/`.q-title` treatment with the app's own semantic
// tokens (primary-filled badge, border hairline divider) rather than the
// prototype's hardcoded hex. Pure chrome: it does not know what facts live
// inside it, so every wrapped sub-component keeps its own internal
// rendering/absence handling exactly as before.
export function QuestionSection({
  number: _number,
  title,
  last = false,
  children,
}: {
  number: 1 | 2 | 3 | 4;
  title: string;
  last?: boolean;
  children: ReactNode;
}) {
  return (
    <section className={cn(!last && "border-b border-border pb-[18px]")}>
      <h4 className="mb-2 font-mono text-[9px] font-semibold uppercase tracking-[0.09em] text-muted-foreground">
        {title}
      </h4>
      <div className="text-[12px] leading-[1.65]">{children}</div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// JS-1097 (adversarial-review fix): Q1's UNBOUND/legacy fallback -- entry-
// derived reason codes, EXPLICITLY labeled as screen-level evidence, never
// presented as if it were a real setup thesis. EXTRACTED from EditionPage.tsx's
// IdeaCard (which had this exact markup inline, unlabeled on any OTHER
// consumer) so every surface that falls back to this state -- the edition
// card AND the ticker view's idea-context banner (which has no row-scoped
// `setup` to bind to at all, so it ALWAYS takes this branch) -- shows the
// SAME "no bound setup thesis" disclosure rather than silently presenting
// bare reason codes as if they were a thesis. NO-TECH-DEBT: one
// implementation, not a second copy of this markup.
// ---------------------------------------------------------------------------

export function WhyNowFallback({ reasonCodes }: { reasonCodes: string[] }) {
  if (reasonCodes.length === 0) {
    return <p className="text-[12px] text-muted-foreground">No reason codes published for this expression.</p>;
  }
  return (
    <div className="space-y-1">
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
        Screen-level evidence (no bound setup thesis)
      </p>
      <ul className="flex flex-wrap gap-1.5">
        {reasonCodes.map((code) => (
          <li key={code}>
            <span className="inline-flex rounded-full border border-border bg-card px-2 py-0.5 text-[11px] text-foreground">
              {reasonCodeLabel(code)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// JS-1097/JS-1104: entry-only compatibility adapter used by the ticker
// context response, which has no enclosing screener row. It delegates to the
// same flat register and therefore renders row-scoped facts as honestly
// absent instead of reconstructing them from unrelated entry fields.
// ---------------------------------------------------------------------------

export interface EntryFourQuestionSetup {
  why_now: string[];
  contradictions?: string[];
  activation?: { status?: string | null } | null;
  invalidation?: { level?: unknown; comparator?: string | null } | null;
  horizon?: { kind?: string | null } | null;
}

export interface EntryFourQuestionAlternative {
  key: string;
  archetype: string | null | undefined;
  isShadow?: boolean;
}

export function EntryFourQuestionArgument({
  entry,
  reasonCodes,
  setup,
  watchOutCodes = [],
  abstainHeadline,
}: {
  entry: MultiStrategyScreenEntry;
  /** Entry-level `reason_codes` -- read by the caller so this component
   * never assumes a specific field name across callers' own entry types. */
  reasonCodes: string[];
  /** A BOUND setup thesis, when the caller's surface has row-scoped setup
   * data to offer (EditionPage's `row.setup`). Absent (never fabricated)
   * for a caller with no row context, e.g. the ticker view. */
  setup?: EntryFourQuestionSetup | null;
  /** Row-derived adverse-signal watch-outs (FLOW_CONTRADICTS /
   * DIRECTION_RS_CONFLICT / EARNINGS_SOON). Absent for a caller with no row
   * to derive them from. */
  watchOutCodes?: string[];
  /** Retained for call-site compatibility with the former four-question
   * adapter; selector rejections now come only from the persisted row field. */
  alternatives?: EntryFourQuestionAlternative[];
  /** Retained for call-site compatibility; the flat register uses its
   * persisted-entry structure helper directly. */
  archetypeLabel: (archetype: string | null | undefined) => string;
  /** The headline reason for an unpriced entry's ARITHMETIC abstain state -- the
   * caller's own `reasonCodeLabel(abstain[0] ?? "DEBIT_ECONOMICS_NO_ELIGIBLE_EXPRESSION")`,
   * injected rather than re-derived here (the fallback code is a screener-
   * specific default the caller owns, not a fact this shared component
   * should hardcode). */
  abstainHeadline: string;
}) {
  return (
    <IdeaDeepDiveSections
      entry={entry}
      reasonCodes={reasonCodes}
      setup={setup}
      watchOutCodes={watchOutCodes}
      abstainHeadline={abstainHeadline}
      idea={entry.expression_best_idea as MultiStrategyBestIdea | null | undefined}
      includeBeAdr
    />
  );
}
